import { Box2, Box3, Mesh, MeshBasicMaterial, PlaneBufferGeometry, Vector2, Vector3 } from 'three';

import {
  Palette,
  TextHorizontalAlignment,
  TextSizingMode,
  TextVerticalAlignment,
} from 'shared/interfaces/planogram';
import { SphereItemType } from 'shared/interfaces/planogram';
import type { BaseObjectUserData } from 'shared/object3d/interfaces';
import type { TextAreaObjectConfig } from 'shared/interfaces/object_config';
import planogramConfig, { PLANOGRAM_CONFIG } from 'shared/config/PlanogramConfig';

import BaseObject3D from 'shared/object3d/BaseObject3D';
import { Text } from 'troika-three-text';
import { resolvePaletteColor } from 'shared/utils/colors';
import { getVisibleText, textMeshRenderSize } from '../utils/TextUtils';
import { assertDefined, assertTrue } from '../utils/debug';

function awaitSync(text: Text): Promise<void> {
  const needsSync = text._needsSync;
  assertDefined(needsSync, 'Troika text needsSync implementation changed');
  if (!needsSync) return Promise.resolve();
  return new Promise(resolve => {
    text.sync(resolve);
  });
}

function alignPosition(
  objectBox: Box2,
  horizontal: TextHorizontalAlignment,
  vertical: TextVerticalAlignment,
  textHeight: number,
): [x: number, y: number] {
  const center = objectBox.getCenter(new Vector2());
  const start = objectBox.min;
  const end = objectBox.max;

  let x: number;
  let y: number;

  switch (horizontal) {
    case TextHorizontalAlignment.CENTER:
      x = center.x;
      break;
    case TextHorizontalAlignment.RIGHT:
      x = end.x;
      break;
    case TextHorizontalAlignment.LEFT:
    default:
      x = start.x;
  }

  switch (vertical) {
    case TextVerticalAlignment.MIDDLE:
      y = center.y + textHeight / 2;
      break;
    case TextVerticalAlignment.BOTTOM:
      y = start.y + textHeight;
      break;
    case TextVerticalAlignment.TOP:
    default:
      y = end.y;
  }
  return [x, y];
}

function align(mesh: Text, config: TextAreaObjectConfig, textHeight: number) {
  const box = new Box2().setFromCenterAndSize(
    new Vector2(0, 0),
    new Vector2(config.scale[0], config.scale[1]),
  );
  const [x, y] = alignPosition(box, config.alignment, config.verticalAlignment, textHeight);
  mesh.position.set(x / config.scale[0], y / config.scale[1], 0);
}

function applyBaseParameters(
  mesh: Text,
  textAreaConfig: TextAreaObjectConfig,
  palette: Palette | undefined,
) {
  mesh.frustumCulled = false;
  const resolvedColor = resolvePaletteColor(textAreaConfig.color, palette);
  mesh.color = resolvedColor;
  mesh.outlineColor = resolvedColor;
  mesh.overflowWrap = textAreaConfig.overflowWrap;
  mesh.font = textAreaConfig.fontFamily.file_url;
  if (textAreaConfig.autoFit)
    mesh.fontSize = textAreaConfig.effectiveFontSize * PLANOGRAM_CONFIG.fontUnitSize;
  else mesh.fontSize = textAreaConfig.fontSize * PLANOGRAM_CONFIG.fontUnitSize;
  mesh.textAlign = textAreaConfig.alignment;
  mesh.anchorX = textAreaConfig.alignment;
  mesh.lineHeight = textAreaConfig.lineHeight / textAreaConfig.fontSize;
  mesh.letterSpacing = textAreaConfig.letterSpacing / textAreaConfig.fontSize;
  mesh.scale.copy(new Vector3(1, 1, 1).divide(new Vector3(...textAreaConfig.scale)));
  mesh.maxWidth = maxWidth(textAreaConfig);
}

function maxWidth(config: TextAreaObjectConfig) {
  if (config.resizingMode === TextSizingMode.FIXED_HEIGHT) return planogramConfig.MAX_OBJECT_SIZE.x;
  else return config.scale[0];
}

const backgroundGeometry = new PlaneBufferGeometry();
const transparentMaterial = new MeshBasicMaterial({
  depthTest: false,
  transparent: true,
  opacity: 0,
});

class TextAreaObject3D extends BaseObject3D<BaseObjectUserData> {
  readonly measureMesh: Text;
  readonly clippedMesh: Text;

  private editMode: boolean;

  constructor() {
    super();

    this.itemType = SphereItemType.TextArea;

    this.measureMesh = new Text();
    this.measureMesh.name = 'MeasureMesh';
    this.add(this.measureMesh);

    this.clippedMesh = new Text();
    this.clippedMesh.name = 'ClippedMesh';
    this.add(this.clippedMesh);

    this.updateVisibility(false);

    const clickableBackgroundMesh = new Mesh(backgroundGeometry, transparentMaterial);
    clickableBackgroundMesh.name = 'TextAreaBackgroundMesh';
    this.add(clickableBackgroundMesh);
  }

  private overflowing = false;

  isOverflowing(): boolean {
    return this.overflowing;
  }

  private async updateMesh(mesh: Text, config: TextAreaObjectConfig, palette: Palette | undefined) {
    assertTrue(mesh === this.clippedMesh || mesh === this.measureMesh, 'Unexpected mesh');
    const isEdit = mesh === this.measureMesh;
    applyBaseParameters(mesh, config, palette);
    mesh.material.opacity = isEdit === this.editMode ? config.alpha : 0;

    await awaitSync(mesh);
    const clippedHeight = textMeshRenderSize(mesh).y;
    align(mesh, config, clippedHeight);
  }

  async configure(config: TextAreaObjectConfig, palette: Palette | undefined) {
    this.measureMesh.text = config.text;
    await this.updateMesh(this.measureMesh, config, palette);
    [this.clippedMesh.text, this.overflowing] = getVisibleText(this.measureMesh, config) ?? [
      '',
      false,
    ];
    await this.updateMesh(this.clippedMesh, config, palette);
  }

  updateVisibility(edit: boolean) {
    if (edit !== this.editMode) {
      const opacity = Math.max(
        this.measureMesh.material.opacity,
        this.clippedMesh.material.opacity,
      );
      this.clippedMesh.material.opacity = edit ? 0 : opacity;
      this.measureMesh.material.opacity = edit ? opacity : 0;
      this.editMode = edit;
    }
  }

  dispose() {
    this.measureMesh.dispose();
    this.clippedMesh.dispose();
  }
}

export default TextAreaObject3D;
